// src/utils/exportIntentions.ts
import { TeachingIntention } from '../types/intentions';
import { exportToCSV, exportToExcel, exportToPDF } from './exportUtils';

/**
 * Helper to format leave type for display
 */
function formatLeaveType(leaveType: string | null | undefined): string {
  if (!leaveType) return '';
  switch (leaveType) {
    case 'long-service': return 'Long Service Leave';
    case 'leave-without-pay': return 'Leave Without Pay';
    case 'other': return 'Other Leave';
    default: return leaveType;
  }
}

/**
 * Helper to format boolean/null interest values
 */
function formatInterest(value: boolean | null | undefined): string {
  if (value === true) return 'Interested';
  if (value === false) return 'Not Interested';
  return 'No Preference';
}

/**
 * Export teaching intentions to a file format
 * @param intentions Array of teaching intentions to export
 * @param format The export format (csv, excel, pdf)
 */
export function exportIntentions(intentions: TeachingIntention[], format: 'csv' | 'excel' | 'pdf'): void {
  // Prepare data for export
  const data = intentions.map(intention => {
    const empDetails = intention.employmentDetails;

    // Derive the intention to return/be considered value
    const returnIntention = empDetails?.employmentType === 'ongoing'
      ? empDetails?.intentionToReturn
      : empDetails?.wishToBeConsidered;

    return {
      'Staff ID': intention.staffId,
      'Staff Name': intention.staffName,
      Department: intention.department,
      Year: intention.year,
      // New employment details fields
      'Employment Type': empDetails?.employmentType || intention.currentEmploymentType,
      'Current FTE': intention.currentFTE,
      'Returning/Considered': returnIntention || intention.intentionToReturn,
      'Not Returning Reason': empDetails?.notReturningReason || '',
      // Leave details
      'Has Leave Plans': empDetails?.hasLeavePlans ? 'Yes' : 'No',
      'Leave Type': formatLeaveType(empDetails?.leaveType),
      'Leave Start Date': empDetails?.leaveStartDate || '',
      'Leave End Date': empDetails?.leaveEndDate || '',
      // Special role interest
      'Year 9 Stretch Interest': formatInterest(empDetails?.year9StretchInterest),
      // TIL activities
      'TIL Activities': empDetails?.tilActivities?.join('; ') || '',
      'TIL Activities Count': empDetails?.tilActivities?.length || 0,
      // Legacy fields for backward compatibility
      'Desired Employment': intention.desiredEmploymentStatus,
      'Target FTE': intention.targetFTE,
      'Preferred Days': intention.preferredDays,
      'Teaching Preferences': rankedPreferenceTexts(intention).join('; '),
      'Comfortable Areas': intention.comfortableTeachingAreas?.join(', '),
      'Additional Comments': intention.additionalComments,
      Status: intention.status,
      Submitted: intention.submittedAt instanceof Date ? intention.submittedAt.toLocaleDateString() : new Date(intention.submittedAt).toLocaleDateString(),
      'Last Updated': intention.lastUpdatedAt instanceof Date ? intention.lastUpdatedAt.toLocaleDateString() : new Date(intention.lastUpdatedAt).toLocaleDateString()
    };
  });
  // Export based on format
  const filename = `teaching_intentions_export_${new Date().toISOString().split('T')[0]}`;
  if (format === 'csv') {
    exportToCSV(data, filename);
  } else if (format === 'excel') {
    exportToExcel(data, filename);
  } else if (format === 'pdf') {
    exportToPDF(data, filename, 'Teaching Intentions Export');
  }
}

/**
 * Helper: format a Date | string | undefined as a short local date string.
 */
function formatDateValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/**
 * Render one teaching-preference entry as text, tolerant of both storage shapes:
 * imported intentions store a plain string (the subject name), while the legacy
 * app shape stored an object ({ learningArea, specificSubject }). Returns '' for
 * anything empty so callers can filter blanks out.
 */
export function teachingPreferenceToText(pref: unknown): string {
  if (pref == null) return '';
  if (typeof pref === 'string') return pref.trim();
  if (typeof pref === 'object') {
    const o = pref as { learningArea?: string; specificSubject?: string };
    const area = o.learningArea ?? '';
    const subject = o.specificSubject ?? '';
    if (area && subject) return `${area}: ${subject}`;
    return (subject || area || '').trim();
  }
  return String(pref);
}

/**
 * The ranked teaching/subject preferences a person submitted, as an ordered
 * list of display strings — tolerant of both storage shapes:
 *  - imported rows: `teachingPreferences` is a string[] (or legacy objects)
 *  - app-submitted rows: the ranked choices live in `preferences.curriculumAreas`
 */
export function rankedPreferenceTexts(intention: TeachingIntention): string[] {
  const fromTp = ((intention.teachingPreferences ?? []) as unknown[])
    .map(teachingPreferenceToText)
    .filter(Boolean);
  if (fromTp.length > 0) return fromTp;

  const areas = (intention.preferences as any)?.curriculumAreas;
  if (Array.isArray(areas) && areas.length > 0) {
    return [...areas]
      .sort((a, b) => (a?.rank ?? 0) - (b?.rank ?? 0))
      .map((a: any) => String(a?.areaName ?? '').trim())
      .filter(Boolean);
  }
  return [];
}

// --- Field extractors: surface what a teacher actually submitted on the form,
// tolerant of both the imported shape and the app-form shape (availability /
// preferences / employmentDetails columns). Each returns a plain display string.

const FTE_PREF_LABEL: Record<string, string> = { '1': '1.0', '0.8': '0.8', '0.75': '0.75', '0.6': '0.6', '0.5': '0.5', '0.4': '0.4', discuss: 'Discuss' };
const DAY_OFF_LABEL: Record<string, string> = {
  'wed-thu': 'Wed & Thu off', 'mon-fri': 'Mon & Fri off',
  'wed-only': 'Wed off', 'fri-only': 'Fri off', none: 'No preference', discuss: 'Discuss',
};

export function employmentTypeText(i: TeachingIntention): string {
  return (i.employmentDetails?.employmentType || i.currentEmploymentType || '') as string;
}
export function ftePreferenceText(i: TeachingIntention): string {
  const p = i.availability?.ftePreference;
  return p ? (FTE_PREF_LABEL[p] ?? String(p)) : '';
}
export function dayOffPreferenceText(i: TeachingIntention): string {
  const d = i.availability?.dayOffPreference;
  return d ? (DAY_OFF_LABEL[d] ?? String(d)) : '';
}
export function yearLevelsText(i: TeachingIntention): string {
  return ((i.preferences as any)?.yearLevels ?? [])
    .map((y: string) => String(y).replace(/^year\s*/i, 'Year '))
    .join(', ');
}
export function subjectsText(i: TeachingIntention): string {
  const subs = (i.preferences as any)?.subjects ?? [];
  return [...subs]
    .sort((a: any, b: any) => (a?.rank ?? 0) - (b?.rank ?? 0))
    .map((s: any) => String(s?.subjectName ?? '').trim())
    .filter(Boolean)
    .join('; ');
}
export function ruahSubjectsText(i: TeachingIntention): string {
  const rs = (i.preferences as any)?.ruahSubjects ?? [];
  const fromStruct = [...rs]
    .sort((a: any, b: any) => (a?.rank ?? 0) - (b?.rank ?? 0))
    .map((s: any) => String(s?.subjectName ?? '').trim())
    .filter(Boolean);
  if (fromStruct.length) return fromStruct.join('; ');
  return (i.ruahPreferences ?? []).join('; '); // imported rows store a string[]
}
export function leaveText(i: TeachingIntention): string {
  const ed = i.employmentDetails;
  if (!ed?.hasLeavePlans) return '';
  const parts = [formatLeaveType(ed.leaveType)];
  if (ed.leaveStartDate || ed.leaveEndDate) parts.push(`${ed.leaveStartDate ?? '?'} → ${ed.leaveEndDate ?? '?'}`);
  return parts.filter(Boolean).join(' ');
}
export function tilActivitiesText(i: TeachingIntention): string {
  return (i.employmentDetails?.tilActivities ?? []).join('; ');
}
export function year9StretchText(i: TeachingIntention): string {
  const v = i.employmentDetails?.year9StretchInterest;
  return v === true ? 'Interested' : v === false ? 'Not interested' : '';
}
export function notReturningReasonText(i: TeachingIntention): string {
  return i.employmentDetails?.notReturningReason ?? '';
}
export function leaveNotesText(i: TeachingIntention): string {
  return (i.availability?.leavePlans ?? '') as string;
}
export function comfortableAreasText(i: TeachingIntention): string {
  return (i.comfortableTeachingAreas ?? []).join('; ');
}

/**
 * Export EVERY field of EVERY intention (complete data dump for offline Excel
 * analysis). Unlike exportIntentions, this ignores column visibility and the
 * tab's status/search filters — callers pass exactly the rows they want dumped.
 * The up-to-six ranked teaching preferences are flattened into their own columns.
 *
 * @param intentions Array of teaching intentions to export
 * @param format The export format (csv, excel, pdf)
 */
export function exportIntentionsFull(
  intentions: TeachingIntention[],
  format: 'csv' | 'excel' | 'pdf'
): void {
  const data = intentions.map(intention => {
    const empDetails = intention.employmentDetails;

    const row: Record<string, string | number> = {
      'Staff ID': intention.staffId ?? '',
      'Staff Name': intention.staffName ?? '',
      Email: intention.email ?? '',
      Department: intention.department ?? '',
      'Secondary Departments': (intention.secondaryDepartments ?? []).join('; '),
      Year: intention.year ?? '',
      Status: intention.status ?? '',
      'Employment Type': employmentTypeText(intention),
      'Intention To Return': intention.intentionToReturn ?? '',
      'Not Returning Reason': notReturningReasonText(intention),
      'FTE Preference': ftePreferenceText(intention),
      'Day-off Preference': dayOffPreferenceText(intention),
      'Target FTE': intention.targetFTE ?? '',
      'Year Levels': yearLevelsText(intention),
      'Subjects': subjectsText(intention),
      'RUAH Subjects': ruahSubjectsText(intention),
      'Preferred Days': intention.preferredDays ?? '',
      'Comfortable Teaching Areas': comfortableAreasText(intention),
      'Leading Learning Role': intention.leadingLearningRole ?? '',
      'Additional Responsibilities': intention.additionalResponsibilities ?? '',
      'Additional Comments': intention.additionalComments ?? '',
      'POL Position': intention.polPosition ?? '',
      'POL Minutes': intention.polMinutes ?? '',
      'Other Allowances': intention.otherAllowances ?? '',
      // Employment details (JSONB) with graceful fallback
      'Has Leave Plans': empDetails?.hasLeavePlans ? 'Yes' : 'No',
      'Leave Type': formatLeaveType(empDetails?.leaveType),
      'Leave Start Date': empDetails?.leaveStartDate ?? '',
      'Leave End Date': empDetails?.leaveEndDate ?? '',
      'Leave Notes': leaveNotesText(intention),
      'Year 9 Stretch Interest': formatInterest(empDetails?.year9StretchInterest),
      'TIL Activities': (empDetails?.tilActivities ?? []).join('; '),
      // HR review + flags
      'Is Late': intention.isLate ? 'Yes' : 'No',
      'HR Discuss Flag': intention.hrDiscussFlag ? 'Yes' : 'No',
      'HR Reviewed By': intention.hrReviewedBy ?? '',
      'HR Reviewed At': formatDateValue(intention.hrReviewedAt),
      'HR Notes': intention.hrNotes ?? '',
      // Audit / source
      'Entered By Staff ID': intention.enteredByStaffId ?? '',
      'Import Source': intention.importSource ?? '',
      'Period ID': intention.periodId ?? '',
      Submitted: formatDateValue(intention.submittedAt),
      'Last Updated': formatDateValue(intention.lastUpdatedAt),
    };

    // Flatten the ranked preferences (subject names for imported rows, ranked
    // curriculum areas for app-submitted rows) into one column per rank.
    const prefTexts = rankedPreferenceTexts(intention);
    for (let rank = 1; rank <= 6; rank++) {
      row[`Pref ${rank}`] = prefTexts[rank - 1] ?? '';
    }

    return row;
  });

  const filename = `teaching_intentions_full_export_${new Date().toISOString().split('T')[0]}`;
  if (format === 'csv') {
    exportToCSV(data, filename);
  } else if (format === 'excel') {
    exportToExcel(data, filename);
  } else if (format === 'pdf') {
    exportToPDF(data, filename, 'Teaching Intentions — Full Export');
  }
}